import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import {
    FiPlus, FiEdit2, FiTrash2, FiUserCheck, FiUserX,
    FiMail, FiCopy, FiCheck
} from 'react-icons/fi';
import './UsersPage.css';

const UsersPage = () => {
    const { user, isSuperAdmin, isAdmin } = useAuth();
    const { showToast, confirm, isOffline } = useUI();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [invitationLink, setInvitationLink] = useState('');
    const [copied, setCopied] = useState(false);
    const [branches, setBranches] = useState([]);

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        permissions: []
    });

    const [inviteData, setInviteData] = useState({
        branchId: '',
        role: 'branch_admin',
        email: ''
    });

    const allPermissions = ['pos', 'inventory', 'reports', 'analytics', 'users', 'settings'];

    useEffect(() => {
        fetchUsers();
        if (isSuperAdmin) {
            fetchBranches();
        }
    }, [isSuperAdmin]);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchBranches = async () => {
        try {
            const response = await api.get('/branches');
            setBranches(response.data);
        } catch (error) {
            console.error('Failed to fetch branches:', error);
        }
    };

    const handleOpenModal = (userData = null) => {
        if (userData) {
            setEditingUser(userData);
            setFormData({
                email: userData.email,
                password: '',
                firstName: userData.first_name,
                lastName: userData.last_name || '',
                phone: userData.phone || '',
                permissions: userData.permissions?.map(p => ({
                    name: p.permission_name,
                    view: p.can_view,
                    create: p.can_create,
                    edit: p.can_edit,
                    delete: p.can_delete
                })) || []
            });
        } else {
            setEditingUser(null);
            setFormData({
                email: '',
                password: '',
                firstName: '',
                lastName: '',
                phone: '',
                permissions: allPermissions.map(p => ({ name: p, view: true, create: false, edit: false, delete: false }))
            });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                permissions: formData.permissions,
                isActive: true
            };

            if (!editingUser) {
                payload.password = formData.password;
            }

            if (editingUser) {
                await api.put(`/users/${editingUser.id}`, payload);
            } else {
                await api.post('/users', payload);
            }

            setShowModal(false);
            fetchUsers();
            showToast('User saved successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to save user', 'error');
        }
    };

    const handleDelete = async (id) => {
        const confirmed = await confirm('Are you sure you want to delete this user?');
        if (!confirmed) return;
        try {
            await api.delete(`/users/${id}`);
            fetchUsers();
            showToast('User deleted successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to delete user', 'error');
        }
    };

    const handleToggleStatus = async (userData) => {
        try {
            await api.patch(`/users/${userData.id}/status`, {
                isActive: !userData.is_active
            });
            fetchUsers();
            showToast('User status updated', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to update status', 'error');
        }
    };

    const handleCreateInvitation = async (e) => {
        e.preventDefault();
        try {
            const response = await api.post('/users/invite', inviteData);
            setInvitationLink(response.data.invitationLink);
            showToast('Invitation link generated', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to create invitation', 'error');
        }
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(invitationLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const togglePermission = (permName, action) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.map(p =>
                p.name === permName ? { ...p, [action]: !p[action] } : p
            )
        }));
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner spinner-lg"></div>
            </div>
        );
    }

    return (
        <div className="users-page">
            <div className="page-header">
                <div>
                    <h1>User Management</h1>
                    <p className="text-muted">Manage staff accounts and permissions</p>
                </div>
                <div className="header-actions">
                    {isSuperAdmin && (
                        <button
                            className="btn btn-outline"
                            onClick={() => {
                                setInvitationLink('');
                                setInviteData({ branchId: branches[0]?.id || '', role: 'branch_admin', email: '' });
                                setShowInviteModal(true);
                            }}
                            disabled={isOffline}
                            title={isOffline ? "Unavailable offline" : ""}
                        >
                            <FiMail /> Send Invitation
                        </button>
                    )}
                    {isAdmin && (
                        <button
                            className="btn btn-primary"
                            onClick={() => handleOpenModal()}
                            disabled={isOffline}
                            title={isOffline ? "Unavailable offline" : ""}
                        >
                            <FiPlus /> Add Staff
                        </button>
                    )}
                </div>
            </div>

            {/* Users Table */}
            <div className="card">
                <div className="table-container">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Role</th>
                                <th>Branch</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u.id} className={!u.is_active ? 'inactive-row' : ''}>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar">
                                                {u.first_name?.charAt(0)}{u.last_name?.charAt(0) || ''}
                                            </div>
                                            <div>
                                                <div className="user-name">{u.first_name} {u.last_name}</div>
                                                <div className="user-email">{u.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`badge badge-${u.role === 'super_admin' ? 'primary' :
                                            u.role === 'branch_admin' ? 'success' : 'gray'
                                            }`}>
                                            {u.role === 'super_admin' ? 'Super Admin' :
                                                u.role === 'branch_admin' ? 'Branch Admin' : 'Staff'}
                                        </span>
                                    </td>
                                    <td>{u.branch_name || '-'}</td>
                                    <td>
                                        <span className={`badge badge-${u.is_active ? 'success' : 'error'}`}>
                                            {u.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            {u.role !== 'super_admin' && (
                                                <>
                                                    <button
                                                        className="btn btn-icon btn-outline"
                                                        onClick={() => handleToggleStatus(u)}
                                                        title={u.is_active ? 'Deactivate' : 'Activate'}
                                                        disabled={isOffline}
                                                    >
                                                        {u.is_active ? <FiUserX /> : <FiUserCheck />}
                                                    </button>
                                                    <button
                                                        className="btn btn-icon btn-outline"
                                                        onClick={() => handleOpenModal(u)}
                                                        disabled={isOffline}
                                                        title={isOffline ? "Unavailable offline" : ""}
                                                    >
                                                        <FiEdit2 />
                                                    </button>
                                                    {isSuperAdmin && (
                                                        <button
                                                            className="btn btn-icon btn-outline btn-danger"
                                                            onClick={() => handleDelete(u.id)}
                                                            disabled={isOffline}
                                                            title={isOffline ? "Unavailable offline" : ""}
                                                        >
                                                            <FiTrash2 />
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add/Edit User Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">
                                {editingUser ? 'Edit User' : 'Add Staff Member'}
                            </h3>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">First Name *</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.firstName}
                                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Last Name</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.lastName}
                                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Email *</label>
                                    <input
                                        type="email"
                                        className="form-input"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                        disabled={!!editingUser}
                                    />
                                </div>

                                {!editingUser && (
                                    <div className="form-group">
                                        <label className="form-label">Password *</label>
                                        <input
                                            type="password"
                                            className="form-input"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                )}

                                <div className="form-group">
                                    <label className="form-label">Phone</label>
                                    <input
                                        type="tel"
                                        className="form-input"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Permissions</label>
                                    <div className="permissions-grid">
                                        <div className="perm-header">
                                            <span>Module</span>
                                            <span>View</span>
                                            <span>Create</span>
                                            <span>Edit</span>
                                            <span>Delete</span>
                                        </div>
                                        {formData.permissions.map(perm => (
                                            <div key={perm.name} className="perm-row">
                                                <span className="perm-name">{perm.name}</span>
                                                {['view', 'create', 'edit', 'delete'].map(action => (
                                                    <label key={action} className="perm-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={perm[action]}
                                                            onChange={() => togglePermission(perm.name, action)}
                                                        />
                                                    </label>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingUser ? 'Update' : 'Add'} User
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Invitation Modal */}
            {showInviteModal && (
                <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">Send Invitation</h3>
                            <button className="modal-close" onClick={() => setShowInviteModal(false)}>
                                &times;
                            </button>
                        </div>
                        {invitationLink ? (
                            <div className="modal-body">
                                <div className="invitation-success">
                                    <FiCheck className="success-icon" />
                                    <h4>Invitation Created!</h4>
                                    <p>Share this link with the new user:</p>
                                    <div className="invitation-link-box">
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={invitationLink}
                                            readOnly
                                        />
                                        <button className="btn btn-primary" onClick={handleCopyLink}>
                                            {copied ? <FiCheck /> : <FiCopy />}
                                            {copied ? 'Copied!' : 'Copy'}
                                        </button>
                                    </div>
                                    <p className="text-muted text-sm mt-2">
                                        This link expires in 7 days.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleCreateInvitation}>
                                <div className="modal-body">
                                    <div className="form-group">
                                        <label className="form-label">Branch *</label>
                                        <select
                                            className="form-select"
                                            value={inviteData.branchId}
                                            onChange={(e) => setInviteData({ ...inviteData, branchId: e.target.value })}
                                            required
                                        >
                                            <option value="">Select Branch</option>
                                            {branches.map(b => (
                                                <option key={b.id} value={b.id}>{b.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Role</label>
                                        <select
                                            className="form-select"
                                            value={inviteData.role}
                                            onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                                        >
                                            <option value="branch_admin">Branch Admin</option>
                                            <option value="staff">Staff</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Email (optional)</label>
                                        <input
                                            type="email"
                                            className="form-input"
                                            value={inviteData.email}
                                            onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                                            placeholder="Pre-fill email for recipient"
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowInviteModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        Generate Link
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersPage;
